import { useEffect, useMemo, useState } from 'react';
import apiService from '../../../services/api';

interface GovernanceOverviewDashboardProps {
  onTooltipShow: (title: string, content: string) => void;
}

type CategoryData = {
  overall_score?: number;
  delta?: number;
};

type GoalsData = {
  avg_progress_percentage?: number;
  completion_percentage?: number;
  goals_on_track?: number;
  goals_completed?: number;
  total_goals?: number;
};

type ActionPlanData = {
  completion_percentage?: number;
  completed_tasks?: number;
  total_tasks?: number;
};

type QuickStats = {
  organic_traffic: {
    current: number;
    previous_month: number;
    delta_pct: number;
  };
  keyword_rankings: {
    current_top10: number;
    previous_week_top10: number;
    delta_since_last_week: number;
  };
  avg_position: {
    current: number;
    baseline_at_lock: number | null;
    improvement_since_lock: number;
  };
};

type TimelineEvent = {
  id: number;
  title: string;
  description: string;
  timestamp: string;
  category?: string;
  delta?: number;
};

const CIRCLE_CIRCUMFERENCE = 201;

const scoreToStrokeOffset = (score: number) => {
  const clamped = Math.max(0, Math.min(100, score));
  return CIRCLE_CIRCUMFERENCE - (CIRCLE_CIRCUMFERENCE * clamped) / 100;
};

const formatNumber = (value: number) => {
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return `${value}`;
};

const formatDelta = (value: number, suffix = '') => {
  const rounded = Number.isInteger(value) ? value : Number(value.toFixed(1));
  return `${rounded >= 0 ? '+' : ''}${rounded}${suffix}`;
};

export default function GovernanceOverviewDashboard({ onTooltipShow }: GovernanceOverviewDashboardProps) {
  const [loading, setLoading] = useState(true);

  const [asisOverall, setAsisOverall] = useState(0);
  const [onPageScore, setOnPageScore] = useState(0);
  const [offPageScore, setOffPageScore] = useState(0);
  const [cwvScore, setCwvScore] = useState(0);

  const [onPageTasks, setOnPageTasks] = useState({ completed: 0, total: 0 });
  const [offPageTasks, setOffPageTasks] = useState({ completed: 0, total: 0 });
  const [cwvTasks, setCwvTasks] = useState({ completed: 0, total: 0 });

  const [goalsData, setGoalsData] = useState<GoalsData>({});
  const [actionPlanData, setActionPlanData] = useState<ActionPlanData>({});
  const [quickStats, setQuickStats] = useState<QuickStats | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);

  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const results = await Promise.allSettled([
          apiService.getGovernanceCategories(userId, 'onpage'),
          apiService.getGovernanceCategories(userId, 'offpage'),
          apiService.getGovernanceCategories(userId, 'cwv'),
          apiService.getGovernanceGoalsPerformance(userId),
          apiService.getActionPlanGovernancePerformance(userId),
          apiService.getGovernanceQuickStats(userId),
          apiService.getGovernanceTimeline(userId, 4),
          apiService.getActionPlanGovernanceCategories(userId),
        ]);

        const getValue = (result: any) => result.status === 'fulfilled' ? (result.value?.data || result.value) : null;

        const onPageRes = getValue(results[0]) || {};
        const offPageRes = getValue(results[1]) || {};
        const cwvRes = getValue(results[2]) || {};
        const goalsRes = getValue(results[3]) || {};
        const actionPlanRes = getValue(results[4]) || {};
        const quickStatsRes = getValue(results[5]) || null;
        const timelineRes = getValue(results[6]) || [];
        const apCategoriesRes = getValue(results[7]) || {};

        const onPageAuditVal = Number(onPageRes.overall_score || 0);
        const offPageAuditVal = Number(offPageRes.overall_score || 0);
        const cwvAuditVal = Number(cwvRes.overall_score || 0);

        const asisCompleteness = (onPageAuditVal + offPageAuditVal + cwvAuditVal) / 3;
        setAsisOverall(Number(asisCompleteness.toFixed(1)));

        // Strategy Action Plan Tasks Completion by Category (replaces raw audit scores)
        const onPageTasksPct = Number(apCategoriesRes.onpage?.completion_percentage || 0);
        const offPageTasksPct = Number(apCategoriesRes.offpage?.completion_percentage || 0);
        const cwvTasksPct = Number(apCategoriesRes.technical?.completion_percentage || 0);

        setOnPageScore(onPageTasksPct);
        setOffPageScore(offPageTasksPct);
        setCwvScore(cwvTasksPct);

        setOnPageTasks({
          completed: Number(apCategoriesRes.onpage?.completed || 0),
          total: Number(apCategoriesRes.onpage?.total || 0),
        });
        setOffPageTasks({
          completed: Number(apCategoriesRes.offpage?.completed || 0),
          total: Number(apCategoriesRes.offpage?.total || 0),
        });
        setCwvTasks({
          completed: Number(apCategoriesRes.technical?.completed || 0),
          total: Number(apCategoriesRes.technical?.total || 0),
        });

        setGoalsData(goalsRes);
        setActionPlanData(actionPlanRes);
        setQuickStats(quickStatsRes);
        setTimeline(timelineRes);
      } catch (error) {
        console.error('Failed to load governance overview data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const hasGoalsConfigured = Boolean(goalsData && goalsData.total_goals && goalsData.total_goals > 0);
  const goalsProgress = hasGoalsConfigured
    ? Number(goalsData.completion_percentage ?? goalsData.avg_progress_percentage ?? 0)
    : 0;
  const actionPlanProgress = Number(actionPlanData.completion_percentage || 0);

  const organicTraffic = quickStats?.organic_traffic.current || 0;
  const organicTrafficDelta = quickStats?.organic_traffic.delta_pct || 0;

  const keywordTop10 = quickStats?.keyword_rankings.current || 0;
  const keywordDeltaWeek = quickStats?.keyword_rankings.delta_since_last_week || 0;

  const avgPosition = quickStats?.avg_position.current || 0;
  const avgPositionImprovement = quickStats?.avg_position.improvement_since_lock || 0;

  const recentActivity = useMemo(() => {
    if (timeline.length > 0) {
      return timeline.map((item) => {
        const category = (item.category || '').toLowerCase();
        let icon = 'ri-checkbox-circle-line';
        let color = 'text-emerald-600';

        if (category.includes('offpage')) {
          icon = 'ri-links-line';
          color = 'text-orange-600';
        } else if (category.includes('technical') || category.includes('cwv')) {
          icon = 'ri-speed-up-line';
          color = 'text-amber-600';
        } else if (category.includes('onpage')) {
          icon = 'ri-edit-line';
          color = 'text-teal-600';
        }

        return {
          icon,
          color,
          text: item.title || item.description || 'Governance update',
          time: item.timestamp ? new Date(item.timestamp).toLocaleString() : 'Recently',
        };
      });
    }

    return [
      { icon: 'ri-checkbox-circle-line', color: 'text-emerald-600', text: 'No recent activity yet', time: 'Waiting for updates' },
    ];
  }, [timeline]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Governance Overview</h2>
          <button
            onClick={() => onTooltipShow('Governance Overview', 'Monitor your SEO health, track performance, and ensure your strategy stays on course. This dashboard provides a comprehensive view of your current state, goals progress, action plan completion, and competitive position.')}
            className="text-gray-400 hover:text-teal-500 cursor-pointer"
          >
            <i className="ri-information-line text-xl"></i>
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400">Comprehensive monitoring of your SEO performance and business growth</p>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500 mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading Governance Metrics...</p>
        </div>
      ) : (
        <>
      {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
        {/* As-Is Performance Score */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between mb-4 min-h-[2rem]">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">As-Is Performance</h3>
            <button
              onClick={() => onTooltipShow('As-Is Performance', 'Overall percentage completeness based on On-Page, Off-Page, and Core Web Vitals governance scores.')}
              className="text-gray-400 hover:text-teal-500 cursor-pointer"
            >
              <i className="ri-information-line"></i>
            </button>
          </div>
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" className="text-gray-200 dark:text-gray-700" />
                <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" strokeDasharray={CIRCLE_CIRCUMFERENCE} strokeDashoffset={scoreToStrokeOffset(asisOverall)} className="text-teal-500" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-gray-900 dark:text-white">{loading ? '-' : Math.round(asisOverall)}</span>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-teal-600 dark:text-teal-400">
            <i className="ri-arrow-up-line mr-1"></i>Completeness score
          </p>
        </div>

        {/* Goals Progress */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between mb-4 min-h-[2rem]">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Goals Progress</h3>
            <button
              onClick={() => onTooltipShow('Goals Progress', 'Progress percentage across completed goals in the current cycle.')}
              className="text-gray-400 hover:text-teal-500 cursor-pointer"
            >
              <i className="ri-information-line"></i>
            </button>
          </div>
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" className="text-gray-200 dark:text-gray-700" />
                <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" strokeDasharray={CIRCLE_CIRCUMFERENCE} strokeDashoffset={scoreToStrokeOffset(goalsProgress)} className="text-emerald-500" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-gray-900 dark:text-white">{loading ? '-' : `${Math.round(goalsProgress)}%`}</span>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-emerald-600 dark:text-emerald-400">
            {hasGoalsConfigured ? (
              <>
                <i className="ri-arrow-up-line mr-1"></i>{(goalsData.goals_completed || 0)}/{(goalsData.total_goals || 0)} goals completed
              </>
            ) : (
              <span className="text-gray-400 dark:text-gray-500">No goals configured</span>
            )}
          </p>
        </div>

        {/* Action Plan Completion */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between mb-4 min-h-[2rem]">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Action Plan</h3>
            <button
              onClick={() => onTooltipShow('Action Plan', 'Progress percentage based on completed tasks out of total action plan tasks.')}
              className="text-gray-400 hover:text-teal-500 cursor-pointer"
            >
              <i className="ri-information-line"></i>
            </button>
          </div>
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" className="text-gray-200 dark:text-gray-700" />
                <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" strokeDasharray={CIRCLE_CIRCUMFERENCE} strokeDashoffset={scoreToStrokeOffset(actionPlanProgress)} className="text-amber-500" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-gray-900 dark:text-white">{loading ? '-' : `${Math.round(actionPlanProgress)}%`}</span>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-amber-600 dark:text-amber-400">
            <i className="ri-arrow-up-line mr-1"></i>{(actionPlanData.completed_tasks || 0)}/{(actionPlanData.total_tasks || 0)} tasks done
          </p>
        </div>
      </div>

      {/* Performance Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* On-Page Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">On-Page SEO</h3>
            <button
              onClick={() => onTooltipShow('On-Page SEO', 'Percentage of On-Page SEO strategy action plan tasks completed.')}
              className="text-gray-400 hover:text-teal-500 cursor-pointer"
            >
              <i className="ri-information-line"></i>
            </button>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Tasks Completed {onPageTasks.total > 0 ? `(${onPageTasks.completed}/${onPageTasks.total})` : ''}
            </span>
            <span className="text-lg font-bold text-emerald-600">{Math.round(onPageScore)}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-300" style={{ width: `${Math.max(0, Math.min(100, onPageScore))}%` }}></div>
          </div>
        </div>

        {/* Off-Page Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Off-Page SEO</h3>
            <button
              onClick={() => onTooltipShow('Off-Page SEO', 'Percentage of Off-Page SEO strategy action plan tasks completed.')}
              className="text-gray-400 hover:text-teal-500 cursor-pointer"
            >
              <i className="ri-information-line"></i>
            </button>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Tasks Completed {offPageTasks.total > 0 ? `(${offPageTasks.completed}/${offPageTasks.total})` : ''}
            </span>
            <span className="text-lg font-bold text-teal-600">{Math.round(offPageScore)}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full transition-all duration-300" style={{ width: `${Math.max(0, Math.min(100, offPageScore))}%` }}></div>
          </div>
        </div>

        {/* Core Web Vitals */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Core Web Vitals</h3>
            <button
              onClick={() => onTooltipShow('Core Web Vitals', 'Percentage of Technical / Core Web Vitals strategy action plan tasks completed.')}
              className="text-gray-400 hover:text-teal-500 cursor-pointer"
            >
              <i className="ri-information-line"></i>
            </button>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Tasks Completed {cwvTasks.total > 0 ? `(${cwvTasks.completed}/${cwvTasks.total})` : ''}
            </span>
            <span className="text-lg font-bold text-amber-600">{Math.round(cwvScore)}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full transition-all duration-300" style={{ width: `${Math.max(0, Math.min(100, cwvScore))}%` }}></div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Organic Traffic</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(organicTraffic)}</p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                <i className={`mr-1 ${organicTrafficDelta >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>{formatDelta(organicTrafficDelta, '%')} since last month
              </p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-2xl text-emerald-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Keyword Rankings</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{keywordTop10}</p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                <i className={`mr-1 ${keywordDeltaWeek >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>{formatDelta(keywordDeltaWeek)} since last week
              </p>
            </div>
            <div className="w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
              <i className="ri-key-2-line text-2xl text-teal-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg. Position</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{avgPosition.toFixed(1)}</p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                <i className={`mr-1 ${avgPositionImprovement >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>{formatDelta(avgPositionImprovement)} since keyword lock
              </p>
            </div>
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
              <i className="ri-trophy-line text-2xl text-amber-600"></i>
            </div>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
